/**
 * useTagSearch Hook
 * Custom hook for tag search with debouncing and infinite scroll support
 */

import { useCallback, useEffect, useRef, useState } from "react"
import { searchTags } from "../services/tag.service"
import type { Tag } from "../types/tag.types"

interface UseTagSearchOptions {
  enabled?: boolean
  pageSize?: number
  debounceMs?: number
  excludeIds?: string[]
}

interface UseTagSearchResult {
  tags: Tag[]
  isLoading: boolean
  isLoadingMore: boolean
  hasMore: boolean
  error: string | null
  searchQuery: string
  setSearchQuery: (query: string) => void
  loadMore: () => Promise<void>
  refresh: () => Promise<void>
  isInitialLoad: boolean
}

/**
 * Hook for searching tags with debouncing and pagination
 *
 * Features:
 * - Debounced search (300ms default)
 * - Shows all user tags + system tags when query is empty
 * - Infinite scroll pagination
 * - Excludes already selected tags
 */
export function useTagSearch(options: UseTagSearchOptions = {}): UseTagSearchResult {
  const { enabled = true, pageSize = 20, debounceMs = 300, excludeIds = [] } = options

  // State
  const [tags, setTags] = useState<Tag[]>([])
  const [searchQuery, setSearchQuery] = useState("")
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isInitialLoad, setIsInitialLoad] = useState(true)

  // Refs for debouncing and avoiding stale closures
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const currentQueryRef = useRef<string>("")
  const currentExcludeIdsRef = useRef<string[]>(excludeIds)
  const prevExcludeIdsLengthRef = useRef(-1) // Initialize to -1 to differentiate from empty array
  const hasLoadedRef = useRef(false) // Track if we've loaded at least once

  // Update excludeIds ref when it changes
  useEffect(() => {
    currentExcludeIdsRef.current = excludeIds
  }, [excludeIds])

  // Reset search when enabled changes
  useEffect(() => {
    if (enabled) {
      setPage(1)
      setHasMore(true)
      setError(null)
    }
  }, [enabled])

  /**
   * Load tags based on current query and page
   */
  const loadTags = useCallback(
    async (query: string, pageNum: number, isLoadMore: boolean = false) => {
      if (!enabled) return

      // Set loading state
      if (isLoadMore) {
        setIsLoadingMore(true)
      } else {
        setIsLoading(true)
        setIsInitialLoad(pageNum === 1 && query === "")
      }
      setError(null)

      try {
        // Search tags with pagination - empty query returns user tags + system tags
        const result = await searchTags({
          query: query || undefined, // Send undefined for empty query to get all tags
          page: pageNum,
          limit: pageSize,
          excludeIds: currentExcludeIdsRef.current,
        })

        // Null safety for data and tags
        const tags = result.data?.tags || []
        const pagination = result.data?.pagination

        if (isLoadMore) {
          // Append new tags to existing list
          setTags(prev => [...prev, ...tags])
        } else {
          // Replace entire list
          setTags(tags)
        }

        if (pagination) {
          setHasMore(pagination.hasMore)
        }
        setPage(pageNum)
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to load tags"
        setError(message)
      } finally {
        setIsLoading(false)
        setIsLoadingMore(false)
        setIsInitialLoad(false)
      }
    },
    [enabled, pageSize],
  )

  /**
   * Debounced search function
   */
  const debouncedSearch = useCallback(
    (query: string) => {
      // Clear existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }

      // Update current query ref
      currentQueryRef.current = query

      // Set new timer
      debounceTimerRef.current = setTimeout(() => {
        // Reset to page 1 when query changes
        setPage(1)
        setHasMore(true)
        loadTags(query, 1, false)
      }, debounceMs)
    },
    [debounceMs, loadTags],
  )

  /**
   * Handle search query change
   */
  const handleSearchQueryChange = useCallback(
    (query: string) => {
      setSearchQuery(query)
      debouncedSearch(query)
    },
    [debouncedSearch],
  )

  /**
   * Load more tags for infinite scroll
   */
  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore || !enabled) return

    const nextPage = page + 1
    await loadTags(currentQueryRef.current, nextPage, true)
  }, [isLoadingMore, hasMore, enabled, page, loadTags])

  /**
   * Refresh current search results
   */
  const refresh = useCallback(async () => {
    setPage(1)
    setHasMore(true)
    await loadTags(currentQueryRef.current, 1, false)
  }, [loadTags])

  /**
   * Initial load on mount + reload when excludeIds changes
   */
  useEffect(() => {
    if (!enabled) return

    const currentLength = excludeIds.length
    const lengthChanged = prevExcludeIdsLengthRef.current !== currentLength

    // Load on first mount or when excludeIds length changes (after initial load)
    if (!hasLoadedRef.current || lengthChanged) {
      loadTags(currentQueryRef.current, 1, false)
      hasLoadedRef.current = true
      prevExcludeIdsLengthRef.current = currentLength
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [enabled, excludeIds.length, loadTags])

  return {
    tags,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    searchQuery,
    setSearchQuery: handleSearchQueryChange,
    loadMore,
    refresh,
    isInitialLoad,
  }
}
